import { MaxNumbersOfSuggestions } from 'constants/suggestion';
import { useMemo } from 'react';
import { SearchFilterMeta } from 'controllers/SearchController/types';
import { useApiFiltersContext } from 'contexts/ApiFiltersContext/ApiFiltersContext';
import getSuggestionsFromFilters from '../helpers/getSuggestionsFromFilters';
import appendSuggestions from '../helpers/appendSuggestions';
import getSuggestionsThatStartsWith from '../helpers/getSuggestionsThatStartsWith';
import {
    ContentSuggestionParams,
    Suggestions,
    SuggestionsType,
} from '../types';
import { useLabels } from './useLabels';
import { usePacks } from './usePacks';

const defaultTrending = [] as Suggestions[];

const useSuggestions = ({
    searchInput,
    hasActiveSearch,
    activeTabTotal,
    filterInstrumentsMeta,
    filterGenresMeta,
    filterSfxsMeta,
    filterTagsMeta,
    hasFilterApplied,
    showOnlyFilterSuggestions,
    trending = defaultTrending,
    recent,
}: {
    searchInput?: string;
    hasActiveSearch: boolean;
    activeTabTotal?: number;
    showOnlyFilterSuggestions: boolean;
    filterInstrumentsMeta?: SearchFilterMeta;
    filterGenresMeta?: SearchFilterMeta;
    filterSfxsMeta?: SearchFilterMeta;
    filterTagsMeta?: SearchFilterMeta;
    hasFilterApplied: boolean;
    trending?: Suggestions[];
    recent: Suggestions[];
}) => {
    const { getFilterLabel } = useApiFiltersContext();

    const suggestionsFromFilters = useMemo(
        () => {
            return activeTabTotal
                ? getSuggestionsFromFilters({
                      total: activeTabTotal,
                      instruments: filterInstrumentsMeta,
                      genres: filterGenresMeta,
                      sfxs: filterSfxsMeta,
                      tags: filterTagsMeta,
                  })
                : [];
        },
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [
            activeTabTotal,
            JSON.stringify(filterInstrumentsMeta),
            JSON.stringify(filterGenresMeta),
            JSON.stringify(filterSfxsMeta),
            JSON.stringify(filterTagsMeta),
        ],
    );

    const {
        labels,
        isLoading: isLoadingLabels,
        hasError: hasLabelsError,
    } = useLabels();

    const {
        packs,
        isLoading: isLoadingPacks,
        hasError: hasPacksError,
    } = usePacks();

    const suggestedLabel = useMemo((): Suggestions | undefined => {
        if (
            !searchInput ||
            hasActiveSearch ||
            isLoadingLabels ||
            hasLabelsError ||
            showOnlyFilterSuggestions
        ) {
            return undefined;
        }

        const firstLabelMatch = labels.find((label) =>
            label.name
                ?.toLocaleLowerCase()
                .startsWith(searchInput.toLocaleLowerCase()),
        );

        if (
            !firstLabelMatch ||
            !firstLabelMatch.slug ||
            !firstLabelMatch.name
        ) {
            return undefined;
        }

        return [
            [ContentSuggestionParams.suggestedlabels, firstLabelMatch.slug],
            SuggestionsType.Label,
        ];
    }, [
        searchInput,
        hasActiveSearch,
        showOnlyFilterSuggestions,
        isLoadingLabels,
        hasLabelsError,
        labels,
    ]);

    const suggestedPack = useMemo((): Suggestions | undefined => {
        if (
            !searchInput ||
            hasActiveSearch ||
            isLoadingPacks ||
            hasPacksError ||
            showOnlyFilterSuggestions
        ) {
            return undefined;
        }

        const firstPackMatch = packs.find((label) =>
            label.name
                ?.toLocaleLowerCase()
                .startsWith(searchInput.toLocaleLowerCase()),
        );

        if (!firstPackMatch || !firstPackMatch.slug || !firstPackMatch.name) {
            return undefined;
        }

        return [
            [ContentSuggestionParams.suggestedpacks, firstPackMatch.slug],
            SuggestionsType.Label,
        ];
    }, [
        searchInput,
        hasActiveSearch,
        showOnlyFilterSuggestions,
        isLoadingPacks,
        hasPacksError,
        packs,
    ]);

    const suggestions = useMemo(() => {
        if (!searchInput) {
            return undefined;
        }

        const filterSuggestions: Suggestions[] = getSuggestionsThatStartsWith(
            suggestionsFromFilters,
            searchInput,
            getFilterLabel,
        );

        const hasSuggestedLabel = !!suggestedLabel;
        const hasSuggestedPack = !!suggestedPack;
        const hasBothContentSuggestion = hasSuggestedLabel && hasSuggestedPack;
        const hasOneContentSuggestion = hasSuggestedLabel || hasSuggestedPack;

        if (!hasOneContentSuggestion) {
            return filterSuggestions;
        }

        const maxSuggestionToRemove = hasBothContentSuggestion ? 2 : 1;
        const suggestionsToAdd = [];

        if (hasBothContentSuggestion) {
            suggestionsToAdd.push(suggestedLabel, suggestedPack);
        } else if (hasSuggestedLabel) {
            suggestionsToAdd.push(suggestedLabel);
        } else if (hasSuggestedPack) {
            suggestionsToAdd.push(suggestedPack);
        }

        // Ensure suggestions are filled with a maximum of 5 filter suggestion, 1 label &/or 1 pack
        filterSuggestions.splice(
            MaxNumbersOfSuggestions - maxSuggestionToRemove,
            maxSuggestionToRemove,
            ...suggestionsToAdd,
        );

        return filterSuggestions;
    }, [
        searchInput,
        suggestionsFromFilters,
        suggestedLabel,
        suggestedPack,
        getFilterLabel,
    ]);

    const defaultSuggestions = useMemo(() => {
        const showTrending = !hasFilterApplied && trending.length > 0;

        return showTrending
            ? appendSuggestions(recent, trending).slice(
                  0,
                  MaxNumbersOfSuggestions,
              )
            : suggestionsFromFilters.slice(0, MaxNumbersOfSuggestions);
    }, [suggestionsFromFilters, trending, recent, hasFilterApplied]);

    return {
        suggestions: suggestions ?? defaultSuggestions,
    };
};

export default useSuggestions;
